import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useUser } from '../../contexts/UserContext';
import { useSchools } from '../../contexts/SchoolsContext';

export interface StudentListItem {
  uid: string;
  firstName: string;
  lastName: string;
  grade: string; // Class Year
  lastActivity: number | null; // Timestamp of last activity from activity_log
  email: string;
  favoriteSchools: string[]; // Array of school names
  favoritePrograms: string[]; // Array of program names (for display)
  favoriteProgramCategories: string[]; // Array of program categories (for filtering)
  favoriteProgramsWithSchools: string[]; // Array of formatted strings: "Program Name - School Name" (for filtering)
  profileImage?: string; // From ProfileData.profileUrl - URL to student's profile picture
  accountType?: number; // AccountType from ProfileData/isStudent: 1=student, 2=parent, 3=guidance, 4=other
}

interface StudentListProps {
  students: StudentListItem[];
  viewMode: 'list' | 'card';
  onStudentClick?: (studentId: string) => void;
}

const StudentList: React.FC<StudentListProps> = ({ students, viewMode, onStudentClick }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useUser();
  const { schools } = useSchools();
  const guidanceUserAvatar = user?.userIcon || undefined;

  // Helper to get spelling based on user's country (Favourite by default, Favorite for US)
  const getFavouriteSpelling = () => {
    return user?.country === 'US' ? 'Favorite' : 'Favourite';
  };

  // Helper to ensure we return undefined instead of null for Avatar src
  const getAvatarSrc = (studentImage?: string | null): string | undefined => {
    const img = studentImage || guidanceUserAvatar;
    return img ?? undefined;
  };

  const handleStudentClick = (studentId: string) => {
    if (onStudentClick) {
      onStudentClick(studentId);
    } else {
      navigate(`/students/${studentId}`);
    }
  };

  // Format name as "Last, First"
  const formatName = (firstName: string, lastName: string) => {
    if (!lastName && !firstName) return 'Unknown';
    if (!lastName) return firstName;
    if (!firstName) return lastName;
    return `${lastName}, ${firstName}`;
  };

  // Format last activity date
  const formatLastActivity = (timestamp: number | null): string => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    } else {
      // For dates older than a year, show the actual date
      return date.toLocaleDateString();
    }
  };

  // Get school logo URL from SchoolsContext by school name
  const getSchoolLogoUrl = (schoolName: string): string | null => {
    const school = schools.find(s => s.name === schoolName);
    if (!school) return null;
    // Prefer feedIcon, fallback to resultsImage/SchoolResultsImageURL
    return school.feedIcon || school.resultsImage || school.SchoolResultsImageURL || null;
  };

  // Helper component for school logo with fallback (circular avatar format)
  const SchoolLogo: React.FC<{ schoolName: string; index: number }> = ({ schoolName, index }) => {
    const [imageError, setImageError] = React.useState(false);
    const logoUrl = getSchoolLogoUrl(schoolName);

    return (
      <Avatar
        key={index}
        sx={{
          width: 32,
          height: 32,
          bgcolor: 'grey.200',
          color: 'grey.600',
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {logoUrl && !imageError ? (
          <img
            src={logoUrl}
            alt={schoolName}
            onError={() => setImageError(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '50%',
              border: '0.5px solid black',
            }}
          />
        ) : (
          schoolName ? schoolName[0].toUpperCase() : ''
        )}
      </Avatar>
    );
  };

  // Render school logos with "+X more" indicator
  const renderSchoolLogos = (schoolNames?: string[]) => {
    const favouriteSpelling = getFavouriteSpelling();
    
    // Handle empty case
    if (!schoolNames || schoolNames.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No {favouriteSpelling.toLowerCase()} schools
        </Typography>
      );
    }

    const maxVisible = 3;
    const visibleSchools = schoolNames.slice(0, maxVisible);
    const remainingCount = schoolNames.length - maxVisible;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {visibleSchools.map((schoolName, index) => (
          <SchoolLogo key={index} schoolName={schoolName} index={index} />
        ))}
        {remainingCount > 0 && (
          <Chip
            label={`+${remainingCount} more`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: '0.75rem', height: 32 }}
          />
        )}
      </Box>
    );
  };

  // Render chips with "+X more" indicator (for programs)
  const renderChips = (items: string[], maxVisible: number = 3, color: 'default' | 'primary' = 'default') => {
    const favouriteSpelling = getFavouriteSpelling();
    // Handle undefined/null or empty array
    if (!items || items.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {color === 'primary' ? `No ${favouriteSpelling.toLowerCase()} schools` : `No ${favouriteSpelling.toLowerCase()} programs`}
        </Typography>
      );
    }

    const visibleItems = items.slice(0, maxVisible);
    const remainingCount = items.length - maxVisible;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {visibleItems.map((item, index) => (
          <Chip
            key={index}
            label={item}
            size="small"
            color={color}
            sx={{ fontSize: '0.75rem' }}
          />
        ))}
        {remainingCount > 0 && (
          <Chip
            label={`+${remainingCount} more`}
            size="small"
            variant="outlined"
            color={color}
            sx={{ fontSize: '0.75rem' }}
          />
        )}
      </Box>
    );
  };

  if (viewMode === 'card') {
    return (
      <Grid container spacing={3}>
        {students.map((student) => (
          <Grid item xs={12} sm={6} md={4} key={student.uid}>
            <Card
              sx={{
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[6],
                },
              }}
              onClick={() => handleStudentClick(student.uid)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={getAvatarSrc(student.profileImage)}
                    alt={formatName(student.firstName, student.lastName)}
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      mr: 2,
                      ...(!student.profileImage && {
                        '& .MuiAvatar-img': {
                          objectFit: 'contain',
                        },
                      }),
                    }}
                  />
                  <Box>
                    <Typography variant="h6" component="div">
                      {formatName(student.firstName, student.lastName)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Class of {student.grade || 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {getFavouriteSpelling()} Schools:
                  </Typography>
                  {renderSchoolLogos(student.favoriteSchools)}
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {getFavouriteSpelling()} Programs:
                  </Typography>
                  {renderChips(student.favoritePrograms, 3, 'default')}
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Last Activity:
                  </Typography>
                  <Typography variant="body2">
                    {formatLastActivity(student.lastActivity)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  // List view (Table)
  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell sx={{ width: 56 }}></TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Class of</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{getFavouriteSpelling()} Schools</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{getFavouriteSpelling()} Programs</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Last Activity</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {students.map((student) => (
            <TableRow
              key={student.uid}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => handleStudentClick(student.uid)}
            >
              <TableCell sx={{ verticalAlign: 'top' }}>
                <Avatar
                  src={getAvatarSrc(student.profileImage)}
                  alt={formatName(student.firstName, student.lastName)}
                  sx={{ 
                    width: 40, 
                    height: 40,
                    ...(!student.profileImage && {
                      '& .MuiAvatar-img': {
                        objectFit: 'contain',
                      },
                    }),
                  }}
                />
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatName(student.firstName, student.lastName)}
                </Typography>
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>{student.grade || 'N/A'}</TableCell>
              <TableCell sx={{ maxWidth: 300, verticalAlign: 'top' }}>
                {renderSchoolLogos(student.favoriteSchools)}
              </TableCell>
              <TableCell sx={{ maxWidth: 300, verticalAlign: 'top' }}>
                {renderChips(student.favoritePrograms, 3, 'default')}
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <Typography variant="body2">
                  {formatLastActivity(student.lastActivity)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StudentList;

